import { useState } from "react";
import { Plus, Loader2, KeyRound, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { MemberAvatar } from "./LockScreen";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export const ROLES = [
  { value: "partner", label: "Partner", hint: "Can add, edit and delete documents & the estate plan" },
  { value: "executor", label: "Executor", hint: "Read-only: sees documents & plan, can't reveal passwords" },
  { value: "health_agent", label: "Healthcare agent", hint: "The person in the healthcare POA: after an ID check, sees only medical papers and can share them with hospitals & doctors" },
  { value: "finance_agent", label: "Financial POA agent", hint: "The person in the financial POA: after an ID check, sees only financial papers, read-only, no passwords" },
  { value: "owner", label: "Owner", hint: "Full access incl. members & billing" },
  { value: "admin", label: "Administrator", hint: "Runs HearthVault itself: every owner power plus the business Admin dashboard (primary vault only)", admin: true },
];
// Administrator appears only where it can be granted: the primary vault, by an Administrator or by an owner while none exists.
export const roleOptions = (canAppointAdmin) => ROLES.filter((r) => !r.admin || canAppointAdmin);

const PIN_RE = /^\d{4,6}$/;

export const MemberRow = ({ m, isMe, isOwner, canAppointAdmin = false, canTouch = true, onRole, onResetPin, onRemove, onEmail, onLang }) => (
  <div data-testid="member-row" className="rounded-lg border border-border bg-card px-3 py-2">
    <div className="flex items-center gap-3">
      <MemberAvatar member={m} size="h-8 w-8 text-xs" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate" data-testid={`member-name-${m.id}`}>
          {m.name}
          {isMe && <span className="ml-1.5 text-[10px] font-mono uppercase text-primary">you</span>}
        </div>
        <div className="text-[11px] text-muted-foreground truncate">
          {m.last_unlock ? `Last unlock ${new Date(m.last_unlock).toLocaleDateString()}` : "Never unlocked"}
          {m.email ? <span data-testid={`member-email-${m.id}`}> · {m.email}</span> : null}
        </div>
      </div>
      <span className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">{m.role_label}</span>
    </div>
    {isOwner && <div className="mt-2 flex items-center gap-1.5 flex-wrap">
    {isOwner && onLang && (
      <Select value={m.lang || "en"} onValueChange={(v) => onLang(m, v)}>
        <SelectTrigger data-testid={`member-lang-${m.id}`} className="h-8 w-[64px] text-[11px] font-mono uppercase bg-background" title="Language for this person's emails and texts">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="en">EN</SelectItem>
          <SelectItem value="es">ES</SelectItem>
        </SelectContent>
      </Select>
    )}
    {isOwner && onEmail && (
      <button
        data-testid={`member-email-btn-${m.id}`}
        onClick={() => onEmail(m)}
        className={`p-1.5 rounded-md hover:bg-accent ${m.email ? "text-muted-foreground hover:text-foreground" : "text-amber-600 hover:text-amber-700"}`}
        title="Edit name & email"
      >
        <Pencil className="h-4 w-4" />
      </button>
    )}
    {isOwner && !isMe && canTouch ? (
      <>
        <span className="flex-1" />
        <Select value={m.role} onValueChange={(v) => onRole(m, v)}>
          <SelectTrigger data-testid={`member-role-${m.id}`} className="h-8 w-[112px] text-xs bg-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {roleOptions(canAppointAdmin || m.role === "admin").map((r) => (
              <SelectItem key={r.value} value={r.value}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          data-testid={`member-reset-pin-${m.id}`}
          onClick={() => onResetPin(m)}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
          title="Reset PIN"
        >
          <KeyRound className="h-4 w-4" />
        </button>
        <button
          data-testid={`member-remove-${m.id}`}
          onClick={() => onRemove(m)}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          title="Remove this person from the vault"
        >
          <Trash2 className="h-4 w-4" /> Remove
        </button>
      </>
    ) : isMe && isOwner ? (
      <span className="flex-1 text-right text-[11px] text-muted-foreground" data-testid={`member-self-hint-${m.id}`}>To remove this profile, sign in as another owner or the Administrator</span>
    ) : null}
    </div>}
  </div>
);

export const MemberEmailForm = ({ target, onSave, onCancel }) => {
  const [name, setName] = useState(target.name || "");
  const [email, setEmail] = useState(target.email || "");
  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ name: name.trim(), email: email.trim() });
  };
  return (
    <form onSubmit={submit} data-testid="member-email-form" className="mt-2 rounded-lg border border-border bg-muted/40 p-3 space-y-2">
      <div className="text-xs text-muted-foreground">Name and email for <b>{target.name}</b>{target.role === "admin" ? " — the email also lets the Administrator sign in with an emailed code instead of the PIN." : target.role === "executor" ? <span data-testid="member-email-executor-hint"> — as executor they'll receive the quarterly recovery-kit copy and executor access-request alerts here.</span> : " — the email is used for the family weekly digest only."}</div>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input data-testid="member-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" maxLength={40} className="bg-background h-9" autoFocus />
        <Input data-testid="member-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" className="bg-background h-9" />
      </div>
      <div className="flex items-center gap-2">
        <Button data-testid="member-email-save-btn" size="sm" type="submit" disabled={!name.trim()}>Save</Button>
        <Button size="sm" variant="ghost" type="button" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
};

export const ResetPinForm = ({ target, onSave, onCancel }) => {
  const [pin, setPin] = useState("");
  return (
    <div className="mt-3 rounded-xl border border-border p-3 flex items-center gap-2">
      <Input
        data-testid="reset-pin-input"
        inputMode="numeric"
        maxLength={6}
        value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
        placeholder={`New PIN for ${target.name}`}
        className="bg-card h-9"
      />
      <Button
        data-testid="reset-pin-save-btn"
        size="sm"
        onClick={() => (PIN_RE.test(pin) ? onSave(pin) : toast.error("PIN must be 4–6 digits"))}
      >
        Save
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
};

export const AddMemberForm = ({ onSave, onCancel, busy, canAppointAdmin = false }) => {
  const [name, setName] = useState("");
  const [role, setRole] = useState("partner");
  const [pin, setPin] = useState("");

  const submit = () => {
    if (!name.trim()) return toast.error("Give them a name");
    if (!PIN_RE.test(pin)) return toast.error("PIN must be 4–6 digits");
    return onSave({ name: name.trim(), role, pin });
  };

  return (
    <div className="mt-3 rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
      <Input data-testid="member-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (e.g. Sam)" className="bg-card" />
      <Select value={role} onValueChange={setRole}>
        <SelectTrigger data-testid="member-role-select" className="bg-card">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {roleOptions(canAppointAdmin).map((r) => (
            <SelectItem key={r.value} value={r.value} data-testid={`member-role-option-${r.value}`}>
              {r.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-[11px] text-muted-foreground -mt-1">{ROLES.find((r) => r.value === role)?.hint}</p>
      <Input
        data-testid="member-pin-input"
        inputMode="numeric"
        maxLength={6}
        value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
        placeholder="Their PIN (4–6 digits)"
        className="bg-card"
      />
      <div className="flex gap-2">
        <Button data-testid="member-save-btn" onClick={submit} disabled={busy} className="flex-1 gap-2 bg-primary hover:bg-primary/90">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add member
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
